/**
 * VIB-1146: Fordel 100 % jevnt per salgsenhet blant percent-eiere, deretter
 * jevnt mellom enhetens medlemmer. Speiler fan-outen i Nytt prosjekt
 * (NewProjectWizard): hver enhet får lik andel, hvert medlem får
 * enhetsandel / antall medlemmer. Eiere uten `sales_unit_id` (legacy) teller
 * som egen enhet. Fixed-eiere ignoreres — de tar sin egen `fixed_amount`-pott.
 */
export interface OwnerSplitInput {
  user_id: string;
  split_type?: "percent" | "fixed" | null;
  sales_unit_id?: string | null;
}

/**
 * Returnerer Map<user_id, ownership_pct> for percent-eierne. Enhets-andelen
 * bruker hele tall med remainder til første enhet (sortert), slik at summen
 * blir nøyaktig 100; medlems-andelen innen en enhet kan være desimal.
 */
export function computeUnitEvenOwnership(
  owners: OwnerSplitInput[],
): Map<string, number> {
  const result = new Map<string, number>();
  const percentOwners = owners.filter(
    (o) => (o.split_type ?? "percent") === "percent",
  );
  if (percentOwners.length === 0) return result;

  // Gruppér per enhet; null sales_unit_id → egen gruppe per bruker (legacy).
  const groups = new Map<string, OwnerSplitInput[]>();
  for (const o of percentOwners) {
    const key = o.sales_unit_id ?? `user:${o.user_id}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(o);
  }

  // Stabil rekkefølge for deterministisk remainder-tildeling.
  const sortedKeys = [...groups.keys()].sort((a, b) => a.localeCompare(b));
  const unitCount = sortedKeys.length;
  const evenUnit = Math.floor(100 / unitCount);
  const remainder = 100 - evenUnit * unitCount;

  sortedKeys.forEach((key, i) => {
    const unitPct = evenUnit + (i === 0 ? remainder : 0);
    const members = groups.get(key)!;
    const memberPct = unitPct / members.length;
    for (const m of members) result.set(m.user_id, memberPct);
  });
  return result;
}
